import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ErrorReporterState, ErrorReportPayload, ErrorSeverity, UserAction } from '@/types/Error.types'
import { collectBrowserInfo, createActionBuffer } from '@/utils/ErrorContext.utils'
import { registerErrorReporter } from '@/utils/GlobalErrorReporter'
import { useAuth } from '@/hooks/UseAuth'
import { ErrorReportFnProvider } from '@/components/shared/ErrorBoundary'

interface ErrorReporterContextValue {
	// State
	state: ErrorReporterState
	recentActions: UserAction[]

	// Actions
	reportError: (error: Error, componentStack?: string) => void
	closeDialog: () => void
	submitReport: (userDescription: string, severity: ErrorSeverity) => Promise<void>
	trackAction: (action: Omit<UserAction, 'timestamp'>) => void
}

const ErrorReporterContext = createContext<ErrorReporterContextValue | null>(null)

interface ErrorReporterProviderProps {
	children: ReactNode
}

const initialState: ErrorReporterState = {
	error: null,
	componentStack: null,
	status: 'idle',
}

/**
 * Provides error reporting functionality throughout the app.
 *
 * Features:
 * - Tracks recent user actions for context
 * - Opens error dialog when reportError is called
 * - Submits error reports to the backend API
 */
export function ErrorReporterProvider({ children }: ErrorReporterProviderProps) {
	const { user } = useAuth()
	const [state, setState] = useState<ErrorReporterState>(initialState)
	const actionBuffer = useRef(createActionBuffer(20))

	// Report an error (opens the dialog)
	const reportError = useCallback((error: Error, componentStack?: string) => {
		setState({
			error,
			componentStack: componentStack ?? null,
			status: 'preview',
		})
	}, [])

	// Close the dialog and reset state
	const closeDialog = useCallback(() => {
		setState(initialState)
	}, [])

	// Submit the error report to the backend
	const submitReport = useCallback(
		async (userDescription: string, severity: ErrorSeverity) => {
			if (!state.error) return

			setState((prev) => ({ ...prev, status: 'submitting' }))

			try {
				const payload: ErrorReportPayload = {
					errorMessage: state.error.message,
					stackTrace: state.error.stack,
					errorName: state.error.name,
					severity,
					browserInfo: collectBrowserInfo(),
					url: window.location.href,
					componentStack: state.componentStack ?? undefined,
					recentActions: actionBuffer.current.getAll(),
					userDescription: userDescription || undefined,
				}

				const response = await fetch('/api/error-reports', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify(payload),
				})

				if (!response.ok) {
					const data = await response.json()
					throw new Error(data.error ?? 'Failed to submit error report')
				}

				const data = await response.json()

				setState({
					error: state.error,
					componentStack: state.componentStack,
					status: 'success',
					reportId: data.reportId,
				})

				// Auto-close after success
				setTimeout(() => {
					setState(initialState)
				}, 2000)
			} catch (err) {
				setState((prev) => ({
					...prev,
					status: 'error',
					submissionError: err instanceof Error ? err.message : 'Failed to submit report',
				}))
			}
		},
		[state.error, state.componentStack],
	)

	// Track a user action for context
	const trackAction = useCallback((action: Omit<UserAction, 'timestamp'>) => {
		actionBuffer.current.add(action)
	}, [])

	// Register with global error reporter to capture errors from outside React
	useEffect(() => {
		return registerErrorReporter(reportError)
	}, [reportError])

	const value = useMemo<ErrorReporterContextValue>(
		() => ({
			state,
			recentActions: actionBuffer.current.getAll(),
			reportError,
			closeDialog,
			submitReport,
			trackAction,
		}),
		[state, reportError, closeDialog, submitReport, trackAction],
	)

	return (
		<ErrorReporterContext.Provider value={value}>
			<ErrorReportFnProvider reportError={reportError}>
				{children}
			</ErrorReportFnProvider>
		</ErrorReporterContext.Provider>
	)
}

/**
 * Hook to access the error reporter context
 */
export function useErrorReporter() {
	const context = useContext(ErrorReporterContext)
	if (!context) {
		throw new Error('useErrorReporter must be used within ErrorReporterProvider')
	}
	return context
}

/**
 * Hook to access just the report function (for use in error boundaries)
 */
export function useReportError() {
	const context = useContext(ErrorReporterContext)
	// Return a no-op if not in provider (allows ErrorBoundary to work standalone)
	return context?.reportError ?? (() => {
	})
}
